import { useEffect, useRef, useState } from 'react';
import { TOOLBAR_HEIGHT } from '../constants';

// raytd/web QA 108.03 item 18 (persisting): this rootMargin decides when a header is considered
// past the sticky boundary and gets promoted into the reserved pinned-header-slot. It was a
// module-level constant fixed to the plain TOOLBAR_HEIGHT, so for SDA views — where the real
// sticky offset is TOOLBAR_HEIGHT + the column-heading row's height (VirtualisedItemList.tsx's
// `stickyTop`) — it fired ~36px too early. The pinned-header-slot then showed the next header
// before the real (still-in-flow) header instance had scrolled up to its own, correctly-offset
// sticky point, rendering both at once with a visible gap/duplicate between them.
export function useSticky(stickyTop: number = TOOLBAR_HEIGHT) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [intersectionTop, setIntersectionTop] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Single node observed → entries always has exactly one element.
    const observer = new window.IntersectionObserver((entries) => {
      const entry = entries[0];
      setIntersectionTop(entry.boundingClientRect.top);
      setIsVisible(entry.isIntersecting && entry.intersectionRatio > 0.95);
    }, {
      threshold: [1, 0],
      // Shift the root boundary down so only elements near the sticky point fire.
      rootMargin: `-${stickyTop + 1}px 0px 0px 0px`,
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [stickyTop]);

  return { ref, isVisible, intersectionTop };
}
